/**
 * Image resizer - simplified from lexical playground
 * https://github.com/facebook/lexical/tree/main/packages/lexical-playground
 */

import type { JSX } from "react";
import { useRef } from "react";

const Direction = {
    east: 1 << 0,
    north: 1 << 3,
    south: 1 << 1,
    west: 1 << 2,
};

function clamp(value: number, min: number, max: number) {
    return Math.min(Math.max(value, min), max);
}

interface ImageResizerProps {
    imageRef: React.RefObject<HTMLImageElement | null>;
    maxWidth: number;
    onResizeMove?: (width: number, height: number) => void;
    onResizeStart: () => void;
    onResizeEnd: (width: number, height: number) => void;
}

export default function ImageResizer({
    imageRef,
    maxWidth,
    onResizeMove,
    onResizeStart,
    onResizeEnd,
}: ImageResizerProps): JSX.Element {
    const positioningRef = useRef<{
        currentHeight: number;
        currentWidth: number;
        direction: number;
        effectiveMaxWidth: number;
        isResizing: boolean;
        ratio: number;
        startHeight: number;
        startWidth: number;
        startX: number;
        startY: number;
    }>({
        currentHeight: 0,
        currentWidth: 0,
        direction: 0,
        effectiveMaxWidth: 0,
        isResizing: false,
        ratio: 0,
        startHeight: 0,
        startWidth: 0,
        startX: 0,
        startY: 0,
    });

    const minWidth = 50;
    const minHeight = 50;
    const absoluteMaxWidth = 2400;

    const handlePointerDown = (event: React.PointerEvent, direction: number) => {
        event.preventDefault();
        const image = imageRef.current;
        if (!image) return;

        const { width, height } = image.getBoundingClientRect();
        const naturalWidth = image.naturalWidth || width;
        const effectiveMaxWidth = Math.max(maxWidth, Math.min(naturalWidth, absoluteMaxWidth));
        const positioning = positioningRef.current;
        positioning.effectiveMaxWidth = effectiveMaxWidth;
        positioning.startWidth = width;
        positioning.startHeight = height;
        positioning.ratio = width / height;
        positioning.currentWidth = width;
        positioning.currentHeight = height;
        positioning.startX = event.clientX;
        positioning.startY = event.clientY;
        positioning.isResizing = true;
        positioning.direction = direction;

        onResizeStart();
        onResizeMove?.(Math.round(width), Math.round(height));

        document.addEventListener("pointermove", handlePointerMove);
        document.addEventListener("pointerup", handlePointerUp);
    };

    const handlePointerMove = (event: PointerEvent) => {
        const image = imageRef.current;
        const positioning = positioningRef.current;
        if (!image || !positioning.isResizing) return;

        const isHorizontal = positioning.direction & (Direction.east | Direction.west);
        const isVertical = positioning.direction & (Direction.south | Direction.north);

        if (isHorizontal && isVertical) {
            let diffX = Math.floor(positioning.startX - event.clientX);
            diffX = positioning.direction & Direction.east ? -diffX : diffX;
            const width = clamp(positioning.startWidth + diffX, minWidth, positioning.effectiveMaxWidth);
            const height = width / positioning.ratio;
            positioning.currentWidth = width;
            positioning.currentHeight = height;
        } else if (isVertical) {
            let diffY = Math.floor(positioning.startY - event.clientY);
            diffY = positioning.direction & Direction.south ? -diffY : diffY;
            const height = clamp(positioning.startHeight + diffY, minHeight, 2000);
            const width = height * positioning.ratio;
            positioning.currentWidth = clamp(width, minWidth, positioning.effectiveMaxWidth);
            positioning.currentHeight = positioning.currentWidth / positioning.ratio;
        } else {
            let diffX = Math.floor(positioning.startX - event.clientX);
            diffX = positioning.direction & Direction.east ? -diffX : diffX;
            const width = clamp(positioning.startWidth + diffX, minWidth, positioning.effectiveMaxWidth);
            const height = width / positioning.ratio;
            positioning.currentWidth = width;
            positioning.currentHeight = height;
        }
        onResizeMove?.(positioning.currentWidth, positioning.currentHeight);
    };

    const handlePointerUp = () => {
        const image = imageRef.current;
        const positioning = positioningRef.current;
        if (image && positioning.isResizing) {
            const width = Math.round(positioning.currentWidth);
            const height = Math.round(positioning.currentHeight);
            positioning.isResizing = false;
            document.removeEventListener("pointermove", handlePointerMove);
            document.removeEventListener("pointerup", handlePointerUp);
            onResizeEnd(width, height);
        }
    };

    return (
        <>
            <span
                className="image-resizer image-resizer-n"
                onPointerDown={(e) => handlePointerDown(e, Direction.north)}
            />
            <span
                className="image-resizer image-resizer-ne"
                onPointerDown={(e) => handlePointerDown(e, Direction.north | Direction.east)}
            />
            <span
                className="image-resizer image-resizer-e"
                onPointerDown={(e) => handlePointerDown(e, Direction.east)}
            />
            <span
                className="image-resizer image-resizer-se"
                onPointerDown={(e) => handlePointerDown(e, Direction.south | Direction.east)}
            />
            <span
                className="image-resizer image-resizer-s"
                onPointerDown={(e) => handlePointerDown(e, Direction.south)}
            />
            <span
                className="image-resizer image-resizer-sw"
                onPointerDown={(e) => handlePointerDown(e, Direction.south | Direction.west)}
            />
            <span
                className="image-resizer image-resizer-w"
                onPointerDown={(e) => handlePointerDown(e, Direction.west)}
            />
            <span
                className="image-resizer image-resizer-nw"
                onPointerDown={(e) => handlePointerDown(e, Direction.north | Direction.west)}
            />
        </>
    );
}
